import { useCallback, useEffect, useMemo, useState } from 'react'
import { AuditLogDto, DevTelephoneNumberDto } from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import DevOtpCard from '../../components/DevOtpCard'
import DevTelephoneModal from '../../components/modals/DevTelephoneModal'
import { adminService } from '../../services/admin.service'
import { useNavigate, useSearchParams } from 'react-router-dom'

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 172
  const paginationHeight = 60

  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

const DevTelephonesPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [entries, setEntries] = useState<DevTelephoneNumberDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<DevTelephoneNumberDto | null>(null)

  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([])
  const [auditPagination, setAuditPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const currentAuditPage = parseInt(searchParams.get('auditPage') || '1')
  const urlAuditPageSize = searchParams.get('auditPageSize')
  const currentAuditPageSize = urlAuditPageSize ? parseInt(urlAuditPageSize) : calculateOptimalPageSize()
  const currentAuditSortOrder = (searchParams.get('auditSortOrder') as 'asc' | 'desc') || 'desc'

  const loadEntries = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminService.getDevTelephones()
      setEntries(response)
    } catch {
      setError('Failed to load dev telephone numbers.')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadAuditLogs = useCallback(async () => {
    try {
      const response = await adminService.getDevTelephoneAuditLogs(
        currentAuditPage,
        currentAuditPageSize,
        '',
        currentAuditSortOrder,
      )
      setAuditLogs(response.auditLogs)
      setAuditPagination(response.pagination)
    } catch (err) {
      console.log('Failed to fetch dev telephone audit logs', err)
    }
  }, [currentAuditPage, currentAuditPageSize, currentAuditSortOrder])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  useEffect(() => {
    loadAuditLogs()
  }, [loadAuditLogs])

  const handleEntrySaved = (saved: DevTelephoneNumberDto) => {
    setEntries(prev => {
      const exists = prev.some(entry => entry.publicId === saved.publicId)
      if (exists) {
        return prev.map(entry => (entry.publicId === saved.publicId ? saved : entry))
      }
      return [...prev, saved].sort((a, b) => a.telephone.localeCompare(b.telephone))
    })
    loadAuditLogs()
  }

  const handleEntryDeleted = (publicId: string) => {
    setEntries(prev => prev.filter(entry => entry.publicId !== publicId))
    loadAuditLogs()
  }

  const handleAuditPageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('auditPage', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleAuditSortChange = (_sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('auditSortOrder', sortOrder)
    newParams.set('auditPage', '1')
    navigate(`?${newParams.toString()}`)
  }

  const auditLogRows = useMemo(
    () =>
      auditLogs.map(log => ({
        ...log,
        data:
          log.data == null
            ? ''
            : typeof log.data === 'string'
              ? log.data
              : JSON.stringify(log.data),
      })),
    [auditLogs],
  )

  if (loading) {
    return <></>
  }

  return (
    <div className="page dev-telephones-page">
      <div className="page-header">
        <h1>Dev telephone numbers</h1>
      </div>

      <p className="roles-page-intro">
        Manage test telephone numbers used for internal users. Numbers in this list receive dev OTP behavior and are
        marked internal on registration.
      </p>

      <div className="card">
        <DevOtpCard />
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="roles-actions">
        <button type="button" onClick={() => setIsCreateModalOpen(true)} className="styled-button sm">
          <span>Add number</span>
        </button>
      </div>

      <div className="roles-table-container">
        <div className="table-wrapper">
          <Table
            id="dev-telephones"
            name="Dev telephone numbers"
            keyField="publicId"
            initialSortColumn="telephone"
            initialSortOrder="asc"
            columns={[
              { label: 'Telephone', accessor: 'telephone', type: 'string' },
              { label: 'Comment', accessor: 'comment', type: 'text' as any },
              { label: 'Updated', accessor: 'updatedAt', type: 'datetime' },
              { label: 'Created', accessor: 'createdAt', type: 'datetime' },
            ]}
            sourceData={entries}
            isPropertyBarVisible={false}
            filterTerms=""
            onRowClick={(row: DevTelephoneNumberDto) =>
              setEditingEntry(entries.find(entry => entry.publicId === row.publicId) ?? null)
            }
          />
        </div>
      </div>

      <div className="users-header" style={{ marginTop: '2rem' }}>
        <h2>Change history</h2>
        <Pagination
          currentPage={auditPagination.page}
          totalPages={auditPagination.totalPages}
          onPageChange={handleAuditPageChange}
        />
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="dev-telephone-audit-logs"
            name="Dev telephone audit logs"
            keyField="publicId"
            initialSortColumn="createdAt"
            initialSortOrder={currentAuditSortOrder}
            columns={[
              { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              { label: 'Event', accessor: 'eventType', type: 'enum', enumName: 'AuditLogEventType' },
              { label: 'Performed by', accessor: 'performedByFullName', type: 'text' as any },
              { label: 'Comment', accessor: 'comment', type: 'text' as any },
              { label: 'Data', accessor: 'data', type: 'text' as any },
            ]}
            sourceData={auditLogRows}
            isPropertyBarVisible={false}
            filterTerms=""
            onSortChange={handleAuditSortChange}
            disableGlobalSearch={true}
            disableClientSort={true}
          />
        </div>
      </div>

      <DevTelephoneModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        entry={null}
        onSaved={handleEntrySaved}
      />

      <DevTelephoneModal
        isOpen={editingEntry !== null}
        onClose={() => setEditingEntry(null)}
        entry={editingEntry}
        onSaved={handleEntrySaved}
        onDeleted={handleEntryDeleted}
      />
    </div>
  )
}

export default DevTelephonesPage
